import { inject, Injectable } from '@angular/core';
import { ProcallConnectivityStep } from '../../models/app/precall/precall-connectivity-step';
import { ConnectivityStore } from '../stores/connectivity.store';
import { VideoSessionService } from './video-session.service';
import { catchError, of, switchMap, tap } from 'rxjs';
import { WebsocketService } from './websocket.service';
import { EnvironmentDetectionService } from 'src/app/shared/services/environment-detection.service';
import {
  BrowserBrand,
  BrowserInfo,
  DeviceType,
  PlatformType,
  webRtcSupported,
} from 'src/libs/browser-compat';

@Injectable({
  providedIn: 'root',
})
export class ConnectivityTestService {
  private readonly videoSessionService = inject(VideoSessionService);
  private readonly websocketService = inject(WebsocketService);
  private readonly envDetectionService = inject(EnvironmentDetectionService);
  readonly store = inject(ConnectivityStore);

  constructor() {
    this.store.update(this.init());
  }

  start() {
    let state = this.init();
    state = { ...state, status: 'in_progress' };
    this.store.update(state);

    const browser: BrowserInfo | null = this.envDetectionService.browserInfo();
    state = {
      ...state,
      browser: {
        type: browser?.brand ?? BrowserBrand.unknown,
        version: browser?.version ?? 'unkown',
        compatibility: this.envDetectionService.isSupported() ?? false,
      },
    };
    this.store.update(state);

    const device = this.envDetectionService.deviceInfo();
    const deviceType = device?.deviceType ?? DeviceType.unknown;
    const platform = device?.platform ?? PlatformType.unknown;
    state = {
      ...state,
      device: {
        type: deviceType,
        platform,
        compatibility: deviceType !== DeviceType.unknown && platform !== PlatformType.unknown,
      },
    };
    this.store.update(state);

    state = { ...state, webRtc: { compatibility: webRtcSupported() } };
    this.store.update(state);

    state = {
      ...state,
      services: {
        websocket: { status: 'in_progress' },
        link: { status: 'in_progress' },
        video: { status: 'in_progress' },
      },
    };
    this.store.update(state);

    this.videoSessionService
      .create()
      .pipe(
        tap(this.onVideoSessionCreated.bind(this)),
        catchError((err) => {
          this.onVideoSessionConnectivityTestError(err);
          this.onVideoSessionError();
          return of(err);
        }),
        switchMap(() => this.websocketService.available()),
        tap(this.onWebsocketAvailabilityCompleted.bind(this)),
        switchMap(() => this.videoSessionService.connectivityTest()),
      )
      .subscribe({
        next: this.onVideoSessionConnectivityTestSuccess.bind(this),
        error: this.onVideoSessionConnectivityTestError.bind(this),
      });
  }

  stop() {
    const current = this.store.snapshot;
    if (!current) {
      console.warn('Connectivity test has not been started yet.');
      return;
    }
    this.store.update({ ...current, status: 'success' });
  }

  private init(): ProcallConnectivityStep {
    return {
      status: 'not_started',
      services: {
        link: { status: 'not_started' },
        video: { status: 'not_started' },
        websocket: { status: 'not_started' },
      },
      browser: undefined,
      device: undefined,
      webRtc: undefined,
    };
  }

  private onVideoSessionCreated() {
    const result = this.store.snapshot;
    if (result?.services) {
      this.store.update({
        ...result,
        services: { ...result.services, link: { status: 'success', available: true } },
      });
    }
  }

  private onVideoSessionError() {
    const result = this.store.snapshot;
    if (result?.services) {
      this.store.update({
        ...result,
        status: result.status || 'failed',
        services: {
          ...result.services,
          link: { status: 'failed', available: false, reason: 'Service unavailable' },
        },
      });
    }
  }

  private onVideoSessionConnectivityTestSuccess() {
    const result = this.store.snapshot;
    if (result?.services) {
      this.store.update({
        ...result,
        status: result.status === 'in_progress' ? 'success' : result.status,
        services: { ...result.services, video: { status: 'success', available: true } },
      });
    }
  }

  private onVideoSessionConnectivityTestError(error: unknown) {
    const result = this.store.snapshot;
    console.error(
      'An error occured when trying to start precall connectivity test on vonage side',
      error,
    );
    if (result?.services) {
      this.store.update({
        ...result,
        status: 'failed',
        services: {
          ...result.services,
          video: { status: 'failed', available: false, reason: 'Service unavailable' },
        },
      });
    }
  }

  private onWebsocketAvailabilityCompleted(available: boolean) {
    const result = this.store.snapshot;
    if (result?.services) {
      this.store.update({
        ...result,
        status: result.status || (available ? 'success' : 'failed'),
        services: {
          ...result.services,
          websocket: {
            status: available ? 'success' : 'failed',
            available,
            reason: available ? undefined : 'Service unavailable',
          },
        },
      });
    }
  }
}
